/**
 * One category across every class you teach.
 *
 * The class view answers "what about this class?"; this answers "where does
 * this stand everywhere?" — which is the question a teacher with several
 * classes actually has when they sit down to grade. It opens on the term being
 * taught rather than the whole year, since the other quarters are history or
 * haven't happened yet.
 */
import { useMemo, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription } from '~/components/ui/card';
import type { SectionKey } from '~/components/Sidebar/contextNav';
import ClassSectionCard from './ClassSectionCard';
import TermFilter from './TermFilter';
import TeachingLoadError from './TeachingLoadError';
import type { TeachingClass, TeachingContext } from './teachingContext';
import { ALL_TERMS, classesInTerm, currentTermId, defaultTermId, termsFromClasses } from './teachingTerms';

const HEADINGS: Partial<Record<SectionKey, { title: string; description: string }>> = {
  people: { title: 'People', description: 'Every student on your rosters, class by class.' },
  assignments: { title: 'Assignments', description: 'The work you have given, across all your classes.' },
  grades: { title: 'Grades', description: 'Where every class stands right now.' },
  attendance: { title: 'Attendance', description: 'Recorded attendance for each class you teach.' },
  standards: { title: 'Standards', description: 'Benchmark progress for each class you teach.' },
};

function TeachingCategoryPage({ section }: { section: SectionKey }) {
  const { schoolSlug, yearSlug } = useParams();
  const { classes, loading, schoolYearName, error } = useOutletContext<TeachingContext>();

  const terms = useMemo(() => termsFromClasses(classes), [classes]);
  const current = useMemo(() => currentTermId(terms), [terms]);
  const fallback = useMemo(() => defaultTermId(terms), [terms]);

  // Null until the classes arrive, so the first render after loading adopts the
  // real default instead of being stuck on a term chosen from an empty list.
  const [chosenTerm, setChosenTerm] = useState<string | null>(null);
  const selectedTerm = chosenTerm ?? fallback;

  const visible = useMemo<TeachingClass[]>(
    () => (selectedTerm ? classesInTerm(classes, selectedTerm) : [...classes]),
    [classes, selectedTerm]
  );

  const heading = HEADINGS[section] ?? { title: String(section), description: '' };

  if (error) return <TeachingLoadError error={error} />;

  if (loading && classes.length === 0) {
    return <Card><CardHeader><CardTitle>Loading…</CardTitle></CardHeader></Card>;
  }

  if (classes.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>No classes yet</CardTitle>
          <CardDescription>
            You aren't assigned to any classes in {schoolYearName}. An admin can add those assignments.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const byTerm = visible.reduce<Record<string, TeachingClass[]>>((acc, offering) => {
    const termName = offering.term?.name ?? '—';
    (acc[termName] ||= []).push(offering);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">{heading.title}</h2>
          <p className="text-muted-foreground">{heading.description}</p>
        </div>
        <TermFilter
          terms={terms}
          value={selectedTerm ?? ALL_TERMS}
          onChange={setChosenTerm}
          currentTermId={current}
        />
      </div>

      {visible.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>No classes in this term</CardTitle>
            <CardDescription>Choose another term to see the classes you teach in it.</CardDescription>
          </CardHeader>
        </Card>
      ) : (
        Object.entries(byTerm).map(([termName, list]) => (
          <div key={termName} className="space-y-3">
            {/* Only worth a term heading when more than one is on screen. */}
            {selectedTerm === ALL_TERMS && (
              <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                {termName}
              </h3>
            )}
            <div className="space-y-4">
              {list.map((offering) => (
                <ClassSectionCard
                  key={offering.id}
                  classOffering={offering}
                  section={section}
                  classPath={`/app/${schoolSlug}/years/${yearSlug}/courses/${offering.course?.slug}/classes/${offering.id}`}
                />
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

export default TeachingCategoryPage;
